import { useEffect, useMemo, useState } from 'react'
import type { InstrumentDto, PricedLeg, SurfaceInput } from '@apna/shared'
import { prefetchChain } from '@/feed/prefetchChain'
import { SPOT_TOKENS } from '@/feed/spotTokens'
import { tickStore } from '@/feed/tickStore'
import { useSnapshot, fetchSnapshot } from '@/feed/useSnapshot'
import { useTickCell, useTickSubscription } from '@/feed/useTick'
import { RISK_FREE_RATE, useChainGreeks, type GreeksRow } from './useChainGreeks'
import { heldPayoff } from './heldPayoff'
import type { OpenBookTrade } from './useOpenBook'

/** Only the selected trade's legs and one parity pair consume quote subscriptions. */
export function useHeldTradePricing(trade: OpenBookTrade, ivMultiplier: number) {
  /*
   * MEMOISED because everything downstream keys off its identity — 9.49.
   *
   * `heldPayoff` is pure, and a fresh object per render made `held.legs` a
   * new array several times a second on a live underlying. That invalidated
   * the payoff chart's `plot` memo (160 samples) and its `todayValues` memo
   * (160 Black-76 valuations per leg) on every tick, for a curve whose
   * inputs — recorded fills — had not changed at all.
   */
  const held = useMemo(() => heldPayoff(trade), [trade])
  const expiry = held.kind === 'READY' ? held.expiry.slice(0, 10) : null
  const [catalogue, setCatalogue] = useState<{ key: string; items: InstrumentDto[] } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const key = `${trade.underlying}|${expiry}`
  useEffect(() => {
    if (!expiry) return
    let cancelled = false
    setError(null)
    void prefetchChain(trade.underlying, expiry).then((res) => {
      if (!cancelled && Array.isArray(res.items)) setCatalogue({ key, items: res.items })
    }).catch(() => { if (!cancelled) setError('The expiry catalogue could not be loaded. Showing the recorded expiry payoff only.') })
    return () => { cancelled = true }
  }, [key, trade.underlying, expiry])

  const spotToken = SPOT_TOKENS[trade.underlying] ?? 0
  const { value: spot } = useTickCell(spotToken, 'ltp')
  const spotTokens = spotToken > 0 ? [spotToken] : []
  useTickSubscription(spotTokens)
  useSnapshot(spotTokens)
  const rows = useMemo(() => {
    const byStrike = new Map<number, GreeksRow>()
    if (catalogue?.key !== key) return []
    for (const item of catalogue.items) {
      const strike = item.strike
      const token = Number(item.brokerToken)
      if (strike === null || !Number.isFinite(strike) || !Number.isInteger(token) || token <= 0) continue
      const row = byStrike.get(strike) ?? { strike, ceToken: null, peToken: null }
      byStrike.set(strike, { ...row, ...(item.optionType === 'CE' ? { ceToken: token } : item.optionType === 'PE' ? { peToken: token } : {}) })
    }
    return [...byStrike.values()]
  }, [catalogue, key])
  const anchor = spot > 0 ? spot : trade.legs.find((l) => l.netQty !== 0)?.strike ?? 0
  const pair = rows.filter((r) => r.ceToken && r.peToken).reduce<GreeksRow | null>((best, r) =>
    !best || Math.abs(r.strike - anchor) < Math.abs(best.strike - anchor) ? r : best, null)
  const legKey = trade.legs.filter((l) => l.netQty !== 0).map((l) => `${l.token}:${l.strike}:${l.optionType}`).join(',')
  const pricingRows = useMemo(() => {
    const selected: GreeksRow[] = trade.legs.filter((l) => l.netQty !== 0).map((l) => ({
      strike: l.strike, ceToken: l.optionType === 'CE' ? l.token : null, peToken: l.optionType === 'PE' ? l.token : null,
    }))
    // solveChain locates the parity strike with find(); its complete pair
    // must precede any one-sided held leg at that same strike.
    if (pair) selected.unshift(pair)
    return selected
    // Quantity and price changes affect payoff, not which quotes are subscribed.
  }, [legKey, pair?.strike, pair?.ceToken, pair?.peToken])
  const tokens = [...new Set(pricingRows.flatMap((r) => [r.ceToken, r.peToken]).filter((t): t is number => t !== null && t > 0))]
  const tokenKey = tokens.join(',')
  useTickSubscription(tokens)
  const greeks = useChainGreeks(pricingRows, { expiry, atmStrike: pair?.strike ?? null })
  useEffect(() => {
    let cancelled = false
    greeks.refresh()
    const missing = tokens.filter((t) => !(tickStore.snapshot(t)?.ltp ?? 0))
    void fetchSnapshot(missing).then(() => { if (!cancelled) greeks.refresh() }).catch(() => {
      if (!cancelled) setError('Some option quotes are unavailable. The expiry payoff remains based on recorded fills.')
    })
    return () => { cancelled = true }
  }, [tokenKey, pair?.strike, expiry, greeks.refresh])

  /*
   * Memoised for the same reason as `held`: the T+0 curve is 160 model
   * valuations per leg, and it is recomputed whenever this object's
   * identity changes rather than whenever its inputs do. `greeks` is
   * already a `useMemo`, so its members are stable between solves.
   */
  const surface: SurfaceInput | null = useMemo(() => {
    if (held.kind !== 'READY' || !(spot > 0) || greeks.forward === null) return null
    const open = trade.legs.filter((l) => l.netQty !== 0)
    if (!open.every((l) => l.token !== null && greeks.byToken.has(l.token))) return null
    const legs: PricedLeg[] = open.map((l, i) => ({ ...held.legs[i]!, iv: greeks.byToken.get(l.token!)!.iv }))
    return { legs, spot, forward: greeks.forward, years: greeks.years, rate: RISK_FREE_RATE, ivMultiplier }
  }, [held, spot, greeks.forward, greeks.years, greeks.byToken, trade.legs, ivMultiplier])
  return { held, surface, spot, why: error ?? 'Waiting for this trade’s quotes and expiry forward. The expiry payoff needs no live-price assumptions.' }
}
